<template>
  <Modal v-model:open="openModal" :trigger="$refs.wizardBtn" :title="$t('Company.CreateCompany')" 
    footer-alignment="center" with-close-button full-width @update:open="onWizardModalClose">
    <Tabs ref="wizardTabs" :tab-nav="tabs" :active-tab="activeTab">
      <template #tabContent-1>
        <WizardGeneralInfo ref="tab1" :company="company" />
      </template>
      <template #tabContent-2>
        <WizardCustomization ref="tab2" :company="company" />
      </template>
      <template #tabContent-3>
        <WizardAuthentication ref="tab3" :company="company" />
      </template>
      <template #tabContent-4>
        <WizardUsers ref="tab4" :company="company" />
      </template>
      <template #tabContent-5>
        <WizardLicenses ref="tab5" :company="company" />
      </template>
      <template #tabContent-6>
        <WizardReview ref="tab6" :company="company" :setup="setup" :status="status" @move="goToTabById" />
      </template>
    </Tabs>

    <template #footer>
      <CGButton color="secondary" :disabled="activeIndex === 0 || setup" @click="goToTabByOffset(-1)">
        <Txt weight="bold" small>
          {{ $t('General.Back') }}
        </Txt>
      </CGButton>
      <CGButton color="primary" :disabled="loading || (setup && !status.step1.success)" @click="handlePrimaryAction">
        {{ buttonLabel }}
      </CGButton>
    </template>
  </Modal>

  <!-- Confirmation Modal -->
  <Modal
    v-model:open="showConfirmClose"
    :title="$t('General.IrreversibleConfirm')"
    footer-alignment="center"
    with-close-button
    small
    @update:open="showConfirmClose = false"
  >
    <p>{{ $t('Company.ConfirmCloseMessage') }}</p>
    <template #footer>
      <CGButton color="secondary" @click="showConfirmClose = false">
        {{ $t('General.Cancel') }}
      </CGButton>
      <CGButton color="primary" @click="confirmCloseWizard">
        {{ $t('General.Confirm') }}
      </CGButton>
    </template>
  </Modal>
</template>

<script>
import { defineComponent } from 'vue';
import companyService from '@/services/company.service';
import localizationService from '@/services/localization.service';
import { ProvisioningType, AuthenticationType, Roles, CyberGuru, ServiceType, LicenseMaxQuantity } from '@/common/constants';

const IDP_PROVIDER = 'saml';

function getInitialCompany() {
  return {
    // Basic company information
    name: '',
    displayname: '',
    legal_company_name: '',
    vat: '',
    country: '',
    timezone: '',
    default_language: '',
    supportAddress: '',
    supported_languages: [],
    boardTrainingType: '0',
    // Customization settings
    primaryColor: '#010309',
    secondaryColor: '#F58100',
    logo: null,
    toggleImage: null,
    logoFilename: 'logo',
    toggleImageFilename: 'togglelogo',
    // Authentication settings
    authType: AuthenticationType.Credentials,
    useMetadataXML: false,
    samlDescriptor: null,
    metadataXML: null,
    xmlFilename: 'CustomerXML',
    // User management settings
    provisioningType: ProvisioningType.Manual,
    enableGamification: false,
    orgName: '',
    // Licenses
    services: [
      { id: 'Awareness', type: ServiceType.Awareness, title: 'Awareness', quantity: 1, enabled: false, license_length: null, start_date: null, variant: 'purple', icon: 'brain' },
      { id: 'Channel', type: ServiceType.Channel, title: 'Channel', quantity: 1, enabled: false, license_length: null, start_date: null, variant: 'blue', icon: 'users' },
      { id: 'Phishing', type: ServiceType.Phishing, title: 'Phishing', quantity: 1, enabled: false, license_length: null, start_date: null, variant: 'red', icon: 'shield' },
      { id: 'PhishPRO', type: ServiceType.Remediation, title: 'Phish PRO', quantity: 1, enabled: false, license_length: null, start_date: null, variant: 'orange', icon: 'medal' },
      { id: 'CA', type: ServiceType.CyberAdvisor, title: 'Cyber Advisor', quantity: 1, enabled: false, license_length: null, start_date: null, variant: 'green', icon: 'bolt' }
    ]
  }
};

export default defineComponent({
	data: function() {
		return {
      tabs: [
        { id: 1, ref: "tab1", name: this.$t('Company.GeneralInfo'), isActive: true, disabled: true },
        { id: 2, ref: "tab2", name: this.$t('Company.Customization'), isActive: false, disabled: true },
        { id: 3, ref: "tab3", name: this.$t('Company.Authentication'), isActive: false, disabled: true },
        { id: 4, ref: "tab4", name: this.$t('Company.UserManagement'), isActive: false, disabled: true },
        { id: 5, ref: "tab5", name: this.$t('Company.Licenses'), isActive: false, disabled: true },
        { id: 6, ref: "tab6", name: this.$t('Company.CompanyCreation'), isActive: false, disabled: true }
      ],
      activeIndex: 0,
      company: getInitialCompany(),
      setup: false,
      status: {},
      openModal: false,
      companyId: null,
      showConfirmClose: false,
      creationSuccess: false,
      LicenseMaxQuantity,
      supportedLanguagesArray: []
    };
  },
  props: {
    companyOptions: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    activeTab() {
      return this.tabs[this.activeIndex];
    },
    buttonLabel() {
      if (this.creationSuccess && this.companyId) {
        return this.$t('Company.CompleteSetup');
      }
      return this.activeIndex === this.tabs.length - 1
        ? this.$t('Company.CreateCompany')
        : this.$t('General.Continue');
    },
    enableSCIM() {
      return this.company.provisioningType === ProvisioningType.Automatic;
    },
    loading() {
      return Object.values(this.status).some(step => step.loading === true);
    },
    supportAddressPlaceholder() {
      if (this.$profile.hasRoles(Roles.MSP)) {
        const parentCompanyEmailDomain = this.$profile.companies.find(c=>c.company_id == this.$profile.company_id)?.email_domain;
        return parentCompanyEmailDomain? `noreply${this.$methods.splitString(parentCompanyEmailDomain)[0]}` : CyberGuru.NoreplyEmail;
      }
      
      return CyberGuru.SupportEmail;
    }
  },
  methods: {
    openWizard() {
      this.openModal = true;
      this.company.supportAddress = this.supportAddressPlaceholder;
      this.company.primaryColor = this.companyOptions?.primary_color || '#010309';
      this.company.secondaryColor = this.companyOptions?.secondary_color || '#F58100';
    },
    onWizardModalClose(val) {
      if (!val) {
        // Modal is being closed
        if (this.loading) {
          // If setup is in progress, prevent closing
          this.$eventBus.$emit('show-alert', { title: this.$t('Company.CannotCloseDuringSetup'), variant: 'warning' });
          this.openModal = true;
          return;
        }
        if(this.setup) {
          // If company creation was successful, allow closing without confirmation
          this.openModal = false;
          this.companyId && this.$emit('company-created');
          this.cleanStatus();
          return;
        }

        if (this.activeIndex > 0 || this.company.name) {
          // If not on the first tab or if some data has been entered, show confirmation
          this.showConfirmClose = true;
          this.openModal = true; // Keep modal open until user confirms
        } else {
          // No data entered, safe to close
          this.openModal = false;
          this.cleanStatus();
        }
      } else {
        // Modal is being opened
        this.openModal = false;
        this.cleanStatus();
      }
    },
    confirmCloseWizard() {
      this.showConfirmClose = false;
      this.openModal = false;
      this.cleanStatus();
    },
    cleanStatus() {
      // Restore modal to initial state
      this.setup = false;
      this.activeIndex = 0;
      this.creationSuccess = false;
      this.resetSetup();
      this.goToTabById(1, true);
      this.company = getInitialCompany();
    },
    resetSetup() {
      this.status = Object.fromEntries(
        Array.from({ length: 5 }, (_, i) => [
          `step${i + 1}`,
          { loading: false, success: false, error: [] }
        ])
      );
      this.companyId = null;
    },
    startSetup() {
      this.setup = true;
      this.createCompany();
    },
    handlePrimaryAction() {
      if(this.creationSuccess && this.companyId) {
        // If company creation was successful, go to company details page
        this.$router.push(this.$getRoleBaseRoute()+'/company/' + this.companyId)
        this.$eventBus.$emit('update:open', false);
        this.cleanStatus();
        return;
      }
      if (this.activeIndex === this.tabs.length - 1) {
        // Last tab, trigger Company creation
        this.startSetup();
      } else {
        // Otherwise, go to next tab
        this.goToTabByOffset(1);
      }
    },
    goToTabById(tabId, skipValidation = false) {
      const targetIndex = this.tabs.findIndex(tab => tab.id === tabId);
      if (targetIndex !== -1) {
        this.goToTabByOffset(targetIndex - this.activeIndex, skipValidation);
      }
    },
    goToTabByOffset(offset, skipValidation = false) {
      const newIndex = this.activeIndex + offset;

      this.triggerTabUpdate();

      if (!skipValidation && offset > 0) {
        try {
          this.validateTab(this.activeIndex + 1);
        } catch (e) {
          this.$eventBus.$emit('show-alert', { title: e.message, variant: 'danger' });
          return;
        }
      }

      if (newIndex >= 0 && newIndex < this.tabs.length) {
        this.activeIndex = newIndex;
        this.tabs.forEach((tab, index) => {
          tab.isActive = index === newIndex;
        });
        // Scroll the tab content to top
        this.$nextTick(() => {
          this.$nextTick(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          })
        })
      }
    },
    triggerTabUpdate() {
      const refName = `tab${this.tabs[this.activeIndex].id}`;
      const tabComponent = this.$refs[refName];
      if (tabComponent && tabComponent.draftCompany) {
        this.updateCompany(tabComponent.draftCompany);
      }
    },
    updateCompany(updatedCompany) {
      this.company = { ...updatedCompany };
    },
    validateTab(tabIndex) {
      if(tabIndex === 1) {
        if ((!this.company.name || this.company.name.trim().length < 3) ||
            (!this.company.displayname || this.company.displayname.trim().length < 3) ||
            (!this.company.legal_company_name || this.company.legal_company_name.trim().length < 3) ||
            (!this.company.vat || this.company.vat.trim().length < 3) ||
            (!this.company.country) ||
            (!this.company.timezone) ||
            (!this.company.supported_languages.length) ||
            (!this.company.default_language) ||
            (!this.company.supportAddress || !/^\S+@\S+\.\S+$/.exec(this.company.supportAddress))) {
          throw new Error(this.$t('Company.ValidationGeneralInfoIncomplete'));
        }
      }
      if(tabIndex === 3) {
        const authType = this.company.authType;
        if ([AuthenticationType.SSO, AuthenticationType.Hybrid].includes(authType)) {
          if (this.company.useMetadataXML && !this.company.metadataXML) {
            throw new Error(this.$t('Company.ValidationMetadataFileRequired'));
          }
          if (!this.company.useMetadataXML && !this.company.samlDescriptor) {
            throw new Error(this.$t('Company.ValidationSAMLDescriptorRequired'));
          }
        }
      }
      if(tabIndex === 4) {
        if (this.company.enableGamification && !this.company.orgName) {
          throw new Error(this.$t('Company.ValidationGamificationCategoryRequired'));
        }
      }
      if(tabIndex === 5) {
        for (const service of this.company.services) {
          if (service.enabled) {
            if (!service.quantity || isNaN(service.quantity) || service.quantity < 1 || service.quantity > this.LicenseMaxQuantity) {
              throw new Error(this.$t('Company.ValidationLicenseQuantityInvalid', { service: service.title }));
            }
            if (!service.start_date) {
              throw new Error(this.$t('Company.ValidationLicenseStartDateRequired', { service: service.title }));
            }
            const startDate = this.$dateTime.fromFormat(service.start_date, 'yyyy-MM-dd');
            if (this.$dateTime.now().minus({ days: 30 }).startOf("day") > startDate.startOf("day")) {
              throw new Error(this.$t('Company.ValidationLicenseStartDateInvalid', { service: service.title }));
            }
          }
        }
      }
    },
    async createCompany() {
      this.resetSetup();


      /** GENERAL INFO - Create company, manage languages and company options */

      // Extract images to upload later
      const toggleImage = this.company.toggleImage;
      this.company.toggleImage = null;
      const logo = this.company.logo;
      this.company.logo = null;
      this.supportedLanguagesArray = this.company.supported_languages;

      // 1. CREATE COMPANY - blocking
      try {
        this.status.step1.loading = true;
        this.company.supported_languages = this.company.supported_languages.map(lang => lang.value);
        const company = await companyService.createCompany(this.company);
        if (!company || !company.data) {
          throw new Error('Company creation failed');
        }

        this.companyId = company.data;

        await companyService.editCompany(this.companyId, { 'sending_week_days': 127, 'sending_time_start': '00:00', 'sending_time_end': '23:59' });
      } catch (error) {
        const errorMessage = error.response?.data?.error?.code ? 'Error.' + error.response.data.error.code : 'Company.CreateCompanyError';

        this.status.step1.error.push(this.$t(errorMessage));
        this.status.step1.success = false;
        this.status.step1.loading = false;
        
        return;
      }

      this.$eventBus.$emit('load-user', 
        async () => { await this.saveCompanyDetails(logo, toggleImage); }, 
        false
      );
    },
    async saveCompanyDetails(logo, toggleImage) {

      // 1.5 Set default language and add supported languages
      try {
        const languages = this.supportedLanguagesArray.map(lang => lang.value);
        await localizationService.addCompanyLang(this.companyId, languages, this.company.default_language);
        await localizationService.setDefaultCompanyLang(this.companyId, this.company.default_language);

      } catch (error) {
        const errorMessage = error.response?.data?.error?.code ? 'Error.' + error.response.data.error.code : 'Company.Step1CatalogError';

        this.status.step1.error.push(this.$t(errorMessage));
        this.status.step1.success = false;
      }

      // 1.8 Save support address and gamification setting (STEP4) - TODO MERGE THIS CALL WITH THE API CALL TO SETUP COMPANY COLORS LATER ON STEP 2
      try {
        const supportEmailAddress = this.company.supportAddress.trim(); 

        const params = [{ 'option_code': 'support_address', 'value': supportEmailAddress }, 
          { 'option_code': 'disable_gamification_company', 'value': !this.company.enableGamification },
          { 'option_code': 'board_training_type_company', 'value': this.company.boardTrainingType }];

        await companyService.updateCompanyOptionValues(this.companyId, params);

        this.status.step1.success = true;
      } catch (error) {
        const errorMessage = error.response?.data?.error?.code ? 'Error.' + error.response.data.error.code : 'Company.Step1CompanyOptionError';

        this.status.step1.error.push(this.$t(errorMessage));
        this.status.step1.success = false;
      } finally {
        this.status.step1.loading = false;
      }


      /** CUSTOMIZATION */

      // 2. UPLOAD LOGOS AND SETUP COLORS
      this.status.step2.loading = true;

      await this.parallelWithStatus([
        this.uploadImage(logo, "logo"),
        this.uploadImage(toggleImage, "togglelogo"),
        companyService.updateCompanyOptionValues(this.companyId, [
          { 'option_code': 'primary_color', 'value': this.company.primaryColor },
          { 'option_code': 'secondary_color', 'value': this.company.secondaryColor }
        ])
      ], this.status.step2, [
        this.$t('Company.Step2LogoError'),
        this.$t('Company.Step2ToggleLogoError'),
        this.$t('Company.Step2ColorError')
      ]);
      // Loading and success are handled in parallelWithStatus
 

      /** AUTHENTICATION SETTINGS */

      // 3. Setup Auth Type and upload metadata URL or XML
      this.status.step3.loading = true;
      if([AuthenticationType.SSO, AuthenticationType.Hybrid].includes(this.company?.authType)) {
        try {
          let payload;
          if (this.company.useMetadataXML && this.company.metadataXML) {
            payload = new FormData();
            payload.append('file', this.company.metadataXML);
            payload.append('displayName', this.company.displayname);
            payload.append('alias', IDP_PROVIDER);
            if(this.company.authType === AuthenticationType.Hybrid) {
              payload.append('allowHybridLogin', true);
            }
          } if (this.company.samlDescriptor) {
            payload = {
              alias: IDP_PROVIDER,
              samlDescriptor: this.company.samlDescriptor,
              displayName: this.company.displayname,
              ...(this.company.authType === AuthenticationType.Hybrid && { allowHybridLogin: true })
            };
          }

          await companyService.createSSOIdentityProvider(this.company.name, payload);
          await companyService.editCompany(this.companyId, { sso_enabled: true });

          this.status.step3.success = true;
        } catch (error) {
          const errorMessage = error.response?.data?.error?.code ? 'Error.' + error.response.data.error.code : 'Company.Step3Error';

          this.status.step3.error.push(this.$t(errorMessage));
          this.status.step3.success = false;
        } finally {
          this.status.step3.loading = false;
        }
      } else {
        // Simulate a delay here to show the user that the step is skipped
        await this.mockApiDelay();
        // Mark the step as successful anyway but it would be more appropriate to mark it as "skipped"
        this.status.step3.success = true;
        this.status.step3.loading = false;
      }

      
      /** USER MANAGEMENT SETTINGS - Set provisioning type and gamification */

      // 4. Setup provisioning type, SCIM and gamification
      this.status.step4.loading = true;
      try {
        if(this.enableSCIM) {
          await companyService.updateScimStatus(this.companyId);
        }

        if(!this.enableSCIM && !(this.company.enableGamification && this.company.orgName)) {
          await this.mockApiDelay();
        }

        this.status.step4.success = true;
      } catch (error) {
        const errorMessage = error.response?.data?.error?.code ? 'Error.' + error.response.data.error.code : 'Company.Step4Error';
        
        this.status.step4.error.push(this.$t(errorMessage));
        this.status.step4.success = false;
      } finally {
        this.status.step4.loading = false;
      }


      /** LICENSES */

      // 5. Assign licenses if any selected
      this.status.step5.loading = true;
      const selectedServices = this.company.services.filter(s => s.enabled);
      if (selectedServices.length > 0) {
        try {
          const licensePromises = selectedServices.map(service => {
            const dueDate = this.$dateTime.fromFormat(service.start_date, 'yyyy-MM-dd').plus({ months: service.license_length }).minus({ day: 1 }).toFormat('dd/MM/yyyy');
            const payload = {
              service_id: service.type,
              quantity: service.quantity,
              license_length: service.license_length,
              start_date: service.start_date,
              due_date: dueDate,
              expiry_date: this.$dateTime.fromFormat(dueDate, "dd/MM/yyyy").toFormat('yyyy-MM-dd'),
              modified_by: this.$user.id,
              assigned_by: this.$user.id
            };

            return companyService.createLicense(this.companyId, payload);
          });
          await this.parallelWithStatus(licensePromises, this.status.step5, selectedServices.map(s => this.$t('Company.Step5LicenseError', { service: s.title })));
          // Loading and success are handled in parallelWithStatus
        } catch (error) {
          const errorMessage = error.response?.data?.error?.code ? 'Error.' + error.response.data.error.code : 'Company.Step5Error';

          this.status.step5.error.push(this.$t(errorMessage));
          this.status.step5.success = false;
          this.status.step5.loading = false;
        }
      } else {
        // Simulate a delay here to show the user that the step is skipped
        await this.mockApiDelay();
        // Mark the step as successful anyway but it would be more appropriate to mark it as "skipped"
        this.status.step5.success = true;
        this.status.step5.loading = false;
      }

      this.creationSuccess = true;
    },
    async uploadImage(image, filename) {
      if (!image) return;

      try {
        const formData = new FormData();
        formData.append('file', image);
        formData.append('mapping', `{"filename": "${filename}"}`);
      
        await companyService.uploadFile(this.companyId, formData);
      } catch (error) {
        throw new Error('Company creation failed');
      }
    },
    async parallelWithStatus(promises, statusObj, errorMsg) {
      const results = await Promise.allSettled(promises);
      let hasError = false;
      results.forEach((result, i) => {
        if (result.status === 'rejected') {
          hasError = true;
          // TODO: improve error handling to show specific error messages from the API if available
          statusObj.error.push(errorMsg[i]);
        }
      });
      statusObj.success = !hasError;
      statusObj.loading = false;
    },
    // Simulate an API delay - useful for skipped steps
    async mockApiDelay() {
      await new Promise(r => setTimeout(r, 1000));
    }
  }
});
</script>

<style lang="scss">
  .cyber-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: .5rem;
  }
  .cyber-card.selected {
    background: rgba(59, 130, 246, 0.05);
    @apply border-blue-200;
  }
</style>